import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { isNil } from 'lodash';
import { MenuItem } from 'primeng/api';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'crm-lk-eu-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './lk-eu-navbar.component.html',
  styleUrls: ['./lk-eu-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class LkEuNavbarComponent implements OnInit, OnDestroy {
  public static readonly HEADER_MOBILE_BURGER_ID = 'lk-eu-header-mobile-burger';

  private static readonly MOBILE_NAV_MAX_WIDTH_PX = 768;
  private static readonly MOBILE_MENU_ANCHOR_GAP_PX = 2;
  private static readonly MOBILE_MENU_VIEWPORT_PAD_PX = 8;
  private static readonly MOBILE_MENU_DEFAULT_WIDTH_PX = 288;

  @Input() public menuItems: MenuItem[] = [];

  @Output() public readonly mobileMenuOpenChange = new EventEmitter<boolean>();

  public activeTopItem: MenuItem | undefined;
  public activeChildItem: MenuItem | undefined;
  public openDropdownLabel: string | null = null;
  public mobileMenuOpen = false;

  @ViewChildren('dropdownPanel')
  private readonly dropdownPanels?: QueryList<ElementRef<HTMLElement>>;

  private navigationSubscription?: Subscription;

  public constructor(
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  public ngOnInit(): void {
    this.syncActiveFromUrl();
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.syncActiveFromUrl();
        this.openDropdownLabel = null;
        this.resetDropdownPanelPositions();
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    const clickInsideNav = !!(target && this.elementRef.nativeElement.contains(target));
    const clickOnHeaderBurger =
      !isNil(target) &&
      (document.getElementById(LkEuNavbarComponent.HEADER_MOBILE_BURGER_ID)?.contains(target) ?? false);

    if (this.mobileMenuOpen && !clickInsideNav && !clickOnHeaderBurger) {
      this.closeMobileMenu();
      this.resetDropdownPanelPositions();
      return;
    }

    if (!this.openDropdownLabel) {
      return;
    }
    if (clickInsideNav) {
      return;
    }
    this.openDropdownLabel = null;
    this.resetDropdownPanelPositions();
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (window.innerWidth > LkEuNavbarComponent.MOBILE_NAV_MAX_WIDTH_PX && this.mobileMenuOpen) {
      this.mobileMenuOpen = false;
      this.resetMobileMenuPanel();
      this.changeDetectorRef.markForCheck();
    } else if (this.mobileMenuOpen) {
      this.changeDetectorRef.detectChanges();
      this.positionMobileMenuPanel();
    }
    if (this.openDropdownLabel && window.innerWidth > LkEuNavbarComponent.MOBILE_NAV_MAX_WIDTH_PX) {
      queueMicrotask(() => {
        const openDropdown = this.elementRef.nativeElement.querySelector('.lk-eu-nav-dropdown--open .lk-eu-nav-tab');
        this.positionDropdownPanel(openDropdown as HTMLElement | null);
      });
    } else {
      this.resetDropdownPanelPositions();
    }
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    if (!this.mobileMenuOpen || window.innerWidth > LkEuNavbarComponent.MOBILE_NAV_MAX_WIDTH_PX) {
      return;
    }
    this.positionMobileMenuPanel();
  }

  public trackByLabel(_index: number, item: MenuItem): string {
    return String(item.label ?? _index);
  }

  public isTopItemActive(item: MenuItem): boolean {
    return this.activeTopItem === item;
  }

  public isChildItemActive(child: MenuItem): boolean {
    return this.activeChildItem === child;
  }

  public getItemHref(item: MenuItem): string | null {
    const base = this.normalizeRouterLink(item.routerLink);
    if (!base) {
      return null;
    }
    if (base.endsWith('/dashboard') || base.endsWith('/settings')) {
      return base;
    }
    if (base.endsWith('/list')) {
      return base;
    }
    return `${base}/list`;
  }

  public toggleDropdown(item: MenuItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const label = String(item.label ?? '');
    const willOpen = this.openDropdownLabel !== label;
    this.openDropdownLabel = willOpen ? label : null;
    this.changeDetectorRef.markForCheck();

    if (willOpen && !this.mobileMenuOpen) {
      queueMicrotask(() => this.positionDropdownPanel(event.currentTarget as HTMLElement | null));
    } else {
      this.resetDropdownPanelPositions();
    }
  }

  public isDropdownOpen(item: MenuItem): boolean {
    return this.openDropdownLabel === String(item.label ?? '');
  }

  public toggleMobileMenu = (): void => {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (!this.mobileMenuOpen) {
      this.openDropdownLabel = null;
      this.resetMobileMenuPanel();
    } else {
      this.changeDetectorRef.detectChanges();
      this.positionMobileMenuPanel();
    }
    this.mobileMenuOpenChange.emit(this.mobileMenuOpen);
    this.changeDetectorRef.markForCheck();
  };

  public closeMobileMenu = (): void => {
    this.mobileMenuOpen = false;
    this.openDropdownLabel = null;
    this.resetMobileMenuPanel();
    this.mobileMenuOpenChange.emit(this.mobileMenuOpen);
    this.changeDetectorRef.markForCheck();
  };

  public onChildLinkClick(): void {
    this.openDropdownLabel = null;
    this.mobileMenuOpen = false;
    this.resetMobileMenuPanel();
    this.resetDropdownPanelPositions();
    this.changeDetectorRef.markForCheck();
  }

  private getMobileMenuElement(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector('#lk-eu-nav-menu');
  }

  private ensureMobileMenuHostedInNav(): void {
    const menu = this.getMobileMenuElement();
    const host = this.elementRef.nativeElement.querySelector('.lk-eu-nav-bar__inner');
    if (isNil(menu) || isNil(host) || menu.parentElement === host) {
      return;
    }
    host.appendChild(menu);
  }

  private positionMobileMenuPanel(): void {
    if (!this.mobileMenuOpen || window.innerWidth > LkEuNavbarComponent.MOBILE_NAV_MAX_WIDTH_PX) {
      return;
    }

    const burger = document.getElementById(LkEuNavbarComponent.HEADER_MOBILE_BURGER_ID);
    const menu = this.getMobileMenuElement();
    if (isNil(burger) || isNil(menu)) {
      return;
    }

    this.ensureMobileMenuHostedInNav();

    const burgerRect = burger.getBoundingClientRect();
    const headerEl = document.querySelector('.lk-eu-header');
    const headerBottom = headerEl?.getBoundingClientRect().bottom;
    const gap = LkEuNavbarComponent.MOBILE_MENU_ANCHOR_GAP_PX;
    const viewportPad = LkEuNavbarComponent.MOBILE_MENU_VIEWPORT_PAD_PX;
    const maxMenuWidth = Math.min(
      LkEuNavbarComponent.MOBILE_MENU_DEFAULT_WIDTH_PX,
      window.innerWidth - viewportPad * 2
    );

    const topAnchor = !isNil(headerBottom) ? headerBottom : burgerRect.bottom;

    menu.style.top = `${topAnchor + gap}px`;
    menu.style.width = `${maxMenuWidth}px`;
    menu.style.marginTop = '0';

    let rightOffset = window.innerWidth - burgerRect.right;
    const menuLeft = window.innerWidth - rightOffset - maxMenuWidth;
    if (menuLeft < viewportPad) {
      rightOffset = window.innerWidth - maxMenuWidth - viewportPad;
    }

    menu.style.left = 'auto';
    menu.style.right = `${rightOffset}px`;

    const menuRight = window.innerWidth - rightOffset;
    const burgerCenterX = burgerRect.left + burgerRect.width / 2;
    const arrowCenterFromMenuRight = menuRight - burgerCenterX;

    menu.style.setProperty('--lk-eu-nav-menu-arrow-offset', `${arrowCenterFromMenuRight}px`);
    menu.classList.add('lk-eu-nav-bar__menu--anchored');
  }

  private resetMobileMenuPanel(): void {
    const menu = this.getMobileMenuElement();
    if (isNil(menu)) {
      return;
    }

    this.ensureMobileMenuHostedInNav();

    menu.style.top = '';
    menu.style.left = '';
    menu.style.right = '';
    menu.style.width = '';
    menu.style.marginTop = '';
    menu.style.removeProperty('--lk-eu-nav-menu-arrow-offset');
    menu.classList.remove('lk-eu-nav-bar__menu--anchored');
  }

  private positionDropdownPanel(anchor: HTMLElement | null): void {
    if (!anchor || window.innerWidth <= LkEuNavbarComponent.MOBILE_NAV_MAX_WIDTH_PX) {
      return;
    }

    const panel = anchor.parentElement?.querySelector('.lk-eu-nav-dropdown__panel') as HTMLElement | null;
    if (!panel) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    panel.style.position = 'fixed';
    panel.style.top = `${rect.bottom + 6}px`;
    panel.style.left = `${rect.left}px`;
    panel.style.right = 'auto';
    panel.style.minWidth = `${Math.max(rect.width, 220)}px`;
  }

  private resetDropdownPanelPositions(): void {
    this.dropdownPanels?.forEach((panelRef) => {
      const panel = panelRef.nativeElement;
      panel.style.position = '';
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.minWidth = '';
    });
  }

  public getActiveChildLabel(item: MenuItem): string | null {
    if (!this.isTopItemActive(item) || !this.activeChildItem?.label) {
      return null;
    }
    return String(this.activeChildItem.label);
  }

  private syncActiveFromUrl(): void {
    const urlPath = this.normalizeLkUrlPath(this.router.url.split('?')[0]);
    this.activeTopItem = this.findActiveTopItem(urlPath);
    this.activeChildItem = this.findActiveChildItem(urlPath, this.activeTopItem);
  }

  private normalizeLkUrlPath(urlPath: string): string {
    if (urlPath === '/lk/external-user' || urlPath === '/lk/external-user/') {
      return '/lk/external-user/dashboard';
    }
    return urlPath;
  }

  private findActiveTopItem(urlPath: string): MenuItem | undefined {
    const candidates: { item: MenuItem; matchLength: number }[] = [];

    for (const item of this.menuItems) {
      let matchLength = 0;
      const parentLink = this.normalizeRouterLink(item.routerLink);
      if (parentLink && this.urlMatchesTabRouterLink(urlPath, parentLink)) {
        matchLength = Math.max(matchLength, parentLink.length);
      }

      item.items?.forEach((child) => {
        const childLink = this.normalizeRouterLink(child.routerLink);
        if (childLink && this.urlMatchesTabRouterLink(urlPath, childLink)) {
          matchLength = Math.max(matchLength, childLink.length);
        }
      });

      if (matchLength > 0) {
        candidates.push({ item, matchLength });
      }
    }

    if (!candidates.length) {
      return undefined;
    }

    return candidates.sort((a, b) => b.matchLength - a.matchLength)[0].item;
  }

  private findActiveChildItem(urlPath: string, topItem?: MenuItem): MenuItem | undefined {
    if (!topItem?.items?.length) {
      return undefined;
    }

    const matches = topItem.items.filter((child) => {
      const childLink = this.normalizeRouterLink(child.routerLink);
      return !isNil(childLink) && this.urlMatchesTabRouterLink(urlPath, childLink);
    });

    if (!matches.length) {
      return undefined;
    }

    return matches.sort(
      (a, b) =>
        (this.normalizeRouterLink(b.routerLink)?.length ?? 0) -
        (this.normalizeRouterLink(a.routerLink)?.length ?? 0)
    )[0];
  }

  private normalizeRouterLink(routerLink: MenuItem['routerLink']): string | null {
    if (isNil(routerLink) || routerLink === '') {
      return null;
    }
    if (typeof routerLink === 'string') {
      return routerLink;
    }
    if (Array.isArray(routerLink)) {
      return this.router.serializeUrl(this.router.createUrlTree(routerLink));
    }
    return null;
  }

  private urlMatchesTabRouterLink(urlPath: string, routerLink: MenuItem['routerLink']): boolean {
    const base = this.normalizeRouterLink(routerLink);
    if (isNil(base)) {
      return false;
    }
    return urlPath === base || urlPath.startsWith(`${base}/`);
  }
}
